import React, { useState, useMemo } from "react";
import "../styles/CryptoBoard.css";
import { GameStyleType } from "./types/types";
import RenderWords from "./hooks/RenderWords";
import { useGame } from "../GameContext";
import formatTime from "./hooks/formatTime";
import RenderGameKey from "./hooks/RenderGameKey";
import checkCryptoValue from "./assets/checkCryptoValue";
import shuffleArray from "./hooks/shuffleArray";
import MESSAGES from "./hooks/message";

const Board: React.FC<GameStyleType> = ({ words, cryptoKey, random }) => {
  const {
    setMessage,
    setGuessesWords,
    setGameStarted,
    setIsTimerRunning,
    isTimerRunning,
    isGameStarted,
    setTime,
    time,
  } = useGame();
  const [roundCount, setRoundCount] = useState<number>(0);

  const [valueInput, setValueInput] = useState<string>("");

  const [winState, setWinState] = useState<boolean>(false);
  const [gameWords, setGameWords] = useState<string[]>(() =>
    random ? shuffleArray(words) : words,
  );
  const [errorInput, setErrorInput] = useState<boolean>(false);
  const isLastWord = roundCount === gameWords.length - 1;

  const checkWord = () => {
    const answer = valueInput.toUpperCase();

    if (gameWords[roundCount].toUpperCase() !== answer) {
      setMessage(MESSAGES.WORD_WRONG);
      setErrorInput(true);
      return;
    }

    setGuessesWords((prev) => [...prev, answer]);

    if (isLastWord) {
      setMessage(MESSAGES.GAME_FINISHED);
      setWinState(true);
    } else {
      setMessage(MESSAGES.ROUND_FINISHED);
    }

    setErrorInput(false);
    setIsTimerRunning(false);
    setValueInput("");
    return;
  };

  const resetRound = () => {
    setValueInput("");
    setMessage("");
    setErrorInput(false);
    setIsTimerRunning(true);
  };

  const nextWord = () => {
    if (winState) {
      resetRound();
      setRoundCount(0);
      setWinState(false);
      setGuessesWords([]);
      setTime(0);
      setGameWords(random ? shuffleArray(words) : words);
      return;
    }
    setRoundCount((prev) => prev + 1);
    resetRound();
    return;
  };

  const RenderButton = () => {
    if (!isGameStarted) {
      return (
        <button
          className="crypto-buttonCheck"
          title="Начать игру"
          onClick={() => {
            setGameStarted(true);
            setIsTimerRunning(true);
            setMessage(MESSAGES.START);
          }}
        >
          Начать игру
          <img
            alt=""
            className="crypto-logoRun"
            src="/img/button/run-fix.svg"
          />
        </button>
      );
    }
    if (winState) {
      return (
        <button className="crypto-buttonCheck" onClick={nextWord}>
          Играть ещё
          <img
            alt=""
            className="crypto-logoRun"
            src="/img/button/run-fix.svg"
          />
        </button>
      );
    }
    return null;
  };

  const validationResult = useMemo(
    () => checkCryptoValue(cryptoKey, words),
    [cryptoKey, words],
  );

  const isValid = validationResult === true;

  if (!isValid) {
    return <div className="crypto-Board" />;
  }

  const isBlockedInput = !isGameStarted || !isTimerRunning;

  return (
    <div className="crypto-Board">
      {isValid && (
        <>
          <div className="crypto-board-content">
            {/* Логотип с кнопками и таймером */}
            <div className="crypto-board-logo">
              <img
                alt="logo"
                src="/img/crypto/person.svg"
                className="crypto-board-logo-image"
              ></img>

              <p className="crypto-subtitle" id="crypto-subtitle-board-logo">
                {isGameStarted && `Таймер: ${formatTime(time)}`}
              </p>
              <div id="crypto-button-board-logo">
                {!isTimerRunning && RenderButton()}
              </div>
            </div>

            <div className="crypto-board-game">
              {/* Графический код */}
              <RenderWords
                gameWords={gameWords}
                roundCount={roundCount}
                cryptoKey={cryptoKey}
                isGameStarted={isGameStarted}
                winState={winState}
              />

              {/* Графический ключ, магический квадрат*/}
              <div className="crypto-board-game-key-wrapper">
                <div
                  className={
                    "crypto-card" + (winState ? " crypto-card-flipped" : "")
                  }
                >
                  <div className="crypto-card-face crypto-card-front">
                    <div className="crypto-board-game-key-word">
                      <RenderGameKey cryptoKey={cryptoKey} />
                    </div>
                  </div>

                  <div className="crypto-card-face crypto-card-back">
                    <img
                      src="/img/crypto/winner.svg"
                      alt="Победа!"
                      className="crypto-win-image"
                    />
                  </div>
                </div>
              </div>

              {/* Поле для ввода и кнопка проверить */}
              <div className="crypto-board-input">
                <div className="crypto-subtitle">Введи ответ:</div>
                <input
                  type="text"
                  value={valueInput}
                  autoComplete="off"
                  disabled={isBlockedInput}
                  onChange={(event) => {
                    setValueInput(event.target.value);
                    setErrorInput(false);
                  }}
                  onFocus={() => {
                    setErrorInput(false);
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      !isBlockedInput &&
                      valueInput.trim()
                    ) {
                      event.preventDefault();
                      checkWord();
                      event.currentTarget.blur();
                    }
                  }}
                  className={
                    "crypto-board-input-field" +
                    (errorInput ? " crypto-input-error" : "")
                  }
                ></input>
                {!isBlockedInput && (
                  <button className="crypto-buttonCheck" onClick={checkWord}>
                    Проверить
                  </button>
                )}
                {(isBlockedInput && isGameStarted && !winState) &&(<button className="crypto-buttonCheck" onClick={nextWord}>
                  Далее
                  <img
                    alt=""
                    className="crypto-logoRun"
                    src="/img/button/run-fix.svg"
                  />
                </button>)
              }
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Board;
